import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { LogIn, Brain, Mail, Lock, AlertCircle } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { LanguageToggle } from '../components/ui/LanguageToggle'
import { ThemeToggle } from '../components/ui/ThemeToggle'
import { useLanguage } from '../contexts/LanguageContext'
import { sanitizeText, validateUserInput, checkRateLimit, logSecurityEvent } from '../utils/security'

export default function LoginPage() {
  const { t } = useLanguage()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [authUser, setAuthUser] = useState<any>(null)

  // Check for existing session
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setAuthUser(session.user)
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthUser(session?.user ?? null)
    })

    return () => subscription.unsubscribe()
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Security: Rate limiting
    if (!checkRateLimit('login_attempt', 5, 60000)) {
      setError('Zu viele Anmeldeversuche. Bitte warten Sie eine Minute.')
      logSecurityEvent('Login rate limit exceeded', { email: email.substring(0, 10) + '...' })
      return
    }

    // Security: Input validation
    const emailValidation = validateUserInput.email(email)
    if (!emailValidation.isValid) {
      setError(emailValidation.error || 'Ungültige E-Mail')
      return
    }

    if (!password || password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein')
      return
    }

    setLoading(true)

    try {
      const mapLoginError = (message: string) => {
        const normalized = message.toLowerCase()

        if (normalized.includes('email not confirmed')) {
          return t('login.error.awaitApproval')
        }

        if (normalized.includes('email logins are disabled')) {
          return t('login.error.emailDisabled')
        }

        return `${t('login.error.generic')} ${message}`
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: sanitizeText(email),
        password: password // Don't sanitize passwords
      })

      if (error) {
        setError(mapLoginError(error.message))
        logSecurityEvent('Login failed', { error: error.message, email: email.substring(0, 10) + '...' })
      } else if (data.user) {
        logSecurityEvent('User logged in', { userId: data.user.id })
        // Redirect handled by auth state change
      }
    } catch (error: any) {
      setError('Ein Fehler ist aufgetreten: ' + error.message)
      logSecurityEvent('Login exception', { error })
    } finally {
      setLoading(false)
    }
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    setAuthUser(null)
  }

  // If user is logged in, show success message
  if (authUser) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-night-950 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <Card className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Brain className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-zinc-100 mb-2">{t('login.success.title')}</h2>
            <p className="text-gray-600 dark:text-slate-200 mb-6">
              {t('login.success.welcome')} {sanitizeText(authUser.email)}
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => window.location.href = '/dashboard'}
                className="w-full bg-purple-500 hover:bg-purple-600 dark:bg-ocean-400 dark:hover:bg-ocean-300"
              >
                🎯 Zum Training Dashboard
              </Button>
              <Button
                onClick={handleSignOut}
                className="w-full bg-gray-500 hover:bg-gray-600 dark:bg-night-700 dark:hover:bg-night-600"
              >
                {t('login.success.logout')}
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-night-950 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <div className="text-center mb-8">
          <a href="/" className="inline-block hover:opacity-80 transition-opacity">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Brain className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-zinc-100">Muay-Thai.app</h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-slate-200 mt-2">{t('footer.powered')}</p>
          </a>
          <div className="mt-4 flex gap-3 justify-center">
            <ThemeToggle />
            <LanguageToggle />
          </div>
        </div>

        <Card className="p-8 dark:bg-night-800/95 dark:border-night-750/40">
          <div className="text-center mb-6">
            <LogIn className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-zinc-100">{t('login.title')}</h2>
            <p className="text-gray-600 dark:text-slate-200">{t('login.subtitle')}</p>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-500/30 rounded-md p-3 mb-4 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-100 mb-1">
                {t('login.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-300" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-night-700 dark:bg-night-900/70 dark:text-slate-100 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-ocean-500 placeholder:text-gray-400 dark:placeholder:text-night-700"
                  placeholder="deine@email.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-100 mb-1">
                {t('login.password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-300" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-night-700 dark:bg-night-900/70 dark:text-slate-100 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-ocean-500 placeholder:text-gray-400 dark:placeholder:text-night-700"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-500 hover:bg-purple-600 dark:bg-ocean-400 dark:hover:bg-ocean-300 disabled:opacity-50"
            >
              {loading ? t('login.loading') : t('login.submit')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-slate-200">
              {t('login.noAccount')}{' '}
              <a href="/beta" className="text-purple-600 hover:text-purple-500 font-medium">
                {t('login.toBeta')}
              </a>
            </p>
          </div>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center">
          <div className="text-xs text-gray-400 dark:text-slate-500">
            {t('footer.joint')}
          </div>
        </div>
      </motion.div>
    </div>
  )
}
